import { useRef, useState } from 'react'
import { Check, Eye, Upload, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import {
  useApproveDocumentRequestItem,
  useRejectDocumentRequestItem,
  useSubmitDocumentRequestItem,
} from '@/hooks/use-document-requests'
import { getApiErrorMessage } from '@/lib/api'
import { isViewableMimeType, viewDocumentById } from '@/lib/documents'
import type {
  DocumentRequest,
  DocumentRequestItem,
  DocumentRequestItemStatus,
} from '@riverpark/types'

function formatDate(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleDateString('en-KE', {
    day: 'numeric',
    month: 'short',
  })
}

function statusBadgeVariant(
  status: DocumentRequestItemStatus,
): 'pending' | 'draft' | 'paid' | 'overdue' {
  if (status === 'pending') return 'pending'
  if (status === 'submitted') return 'draft'
  if (status === 'approved') return 'paid'
  return 'overdue'
}

function requestStatusLabel(request: DocumentRequest): string {
  return request.status.replace('_', ' ')
}

function ItemRow({
  requestId,
  item,
  isStaff,
  isOwner,
}: {
  requestId: string
  item: DocumentRequestItem
  isStaff: boolean
  isOwner: boolean
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const submitItem = useSubmitDocumentRequestItem()
  const approveItem = useApproveDocumentRequestItem()
  const rejectItem = useRejectDocumentRequestItem()
  const [rejecting, setRejecting] = useState(false)
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  const canUpload = isOwner && (item.status === 'pending' || item.status === 'rejected')
  const canDecide = isStaff && item.status === 'submitted'

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setError(null)
    try {
      await submitItem.mutateAsync({ requestId, itemId: item.id, file })
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not upload file'))
    } finally {
      e.target.value = ''
    }
  }

  async function handleApprove() {
    setError(null)
    try {
      await approveItem.mutateAsync({ requestId, itemId: item.id })
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not approve'))
    }
  }

  async function handleReject() {
    if (!note.trim()) {
      setError('A note explaining what needs fixing is required')
      return
    }
    setError(null)
    try {
      await rejectItem.mutateAsync({ requestId, itemId: item.id, note: note.trim() })
      setRejecting(false)
      setNote('')
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not send back for revision'))
    }
  }

  return (
    <li className="px-4 py-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Badge variant={statusBadgeVariant(item.status)}>{item.status.replace('_', ' ')}</Badge>
          <p className="font-sans text-sm font-medium text-ink">{item.label}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {item.documentId && (
            <button
              type="button"
              onClick={() =>
                item.documentMimeType && isViewableMimeType(item.documentMimeType)
                  ? void viewDocumentById(item.documentId!)
                  : undefined
              }
              className="flex items-center gap-1 font-sans text-xs font-medium text-muted-foreground hover:text-primary"
            >
              <Eye size={13} />
              View
            </button>
          )}

          {canUpload && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => void handleFileChange(e)}
              />
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                loading={submitItem.isPending}
              >
                <Upload size={13} />
                Upload
              </Button>
            </>
          )}

          {canDecide && !rejecting && (
            <>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="border-destructive/40 text-destructive hover:border-destructive"
                onClick={() => setRejecting(true)}
              >
                <X size={13} />
                Send back
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => void handleApprove()}
                loading={approveItem.isPending}
              >
                <Check size={13} />
                Approve
              </Button>
            </>
          )}
        </div>
      </div>

      {item.reviewNote && item.status === 'rejected' && (
        <p className="mt-1.5 font-sans text-xs text-destructive">
          Revision needed: {item.reviewNote}
        </p>
      )}

      {rejecting && (
        <div className="mt-2 space-y-2">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="What needs to be fixed?"
            className="flex w-full rounded border border-input bg-card px-3 py-1.5 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20"
          />
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="secondary" onClick={() => setRejecting(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => void handleReject()}
              loading={rejectItem.isPending}
            >
              Send back for revision
            </Button>
          </div>
        </div>
      )}

      {error && (
        <p className="mt-1.5 font-sans text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </li>
  )
}

export function DocumentRequestCard({ request }: { request: DocumentRequest }) {
  const { user } = useAuth()
  const isStaff =
    user?.role === 'admin' || user?.role === 'committee' || user?.role === 'estate_manager'
  const isOwner = user?.id === request.memberId

  return (
    <div className="rounded-card border border-border-2 bg-card">
      <div className="flex items-start justify-between gap-3 border-b border-border-2 px-4 py-3">
        <div>
          <p className="font-sans text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground">
            Plot {request.plotNumber} · {request.memberName}
          </p>
          <h3 className="font-serif text-base text-ink">{request.title}</h3>
          {request.instructions && (
            <p className="mt-0.5 font-sans text-xs text-muted-foreground">{request.instructions}</p>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <Badge
            variant={
              request.status === 'approved'
                ? 'paid'
                : request.status === 'needs_revision'
                  ? 'overdue'
                  : request.status === 'submitted'
                    ? 'draft'
                    : 'pending'
            }
          >
            {requestStatusLabel(request)}
          </Badge>
          {request.dueDate && (
            <p className="font-sans text-[11px] text-muted-foreground">
              Due {formatDate(request.dueDate)}
            </p>
          )}
        </div>
      </div>
      <ul className="divide-y divide-border-2">
        {request.items.map((item) => (
          <ItemRow
            key={item.id}
            requestId={request.id}
            item={item}
            isStaff={isStaff}
            isOwner={isOwner}
          />
        ))}
      </ul>
    </div>
  )
}
